import { apiBoost, apiMetrics } from "@/common/services";
import {
  AddedToCollectionEvent,
  BoostProfileClickedEvent,
  DailyVisitEvent,
  FollowUserClickedEvent,
  GifLikedEvent,
  GifPlaybackEvent,
  GifSharedEvent,
  GifVideoLoadEvent,
  GifViewEvent,
  LiveCamClickedEvent,
  LiveCamViewEvent,
  LiveCircleClickedEvent,
  PaidLinksClickedEvent,
  PaidLinksViewedEvent,
  PlayerSocialLinkClickedEvent,
  ProfileClickedEvent,
  EmbedRevenueLinkClickedEvent,
  RevenueLinkType,
  SocialProfileClickedEvent, FeedSwitchedEvent,
} from "@/common/buses";

enum EventTypes {
  gifView = "gifView",
  gifPlayback = "gifPlayback",
  gifVideoLoad = "gifVideoLoad",
  gifShared = "gifShared",
  addedToCollection = "addedToCollection",
  gifLiked = "gifLiked",
  profileClicked = "profileClicked",
  boostProfileClicked = "boostProfileClicked",
  followUserClicked = "followUserClicked",
  dailyVisit = "dailyVisit",
  liveCircleClicked = "liveCircleClicked",
  nicheSearched = "nicheSearched",
  liveCamClicked = "liveCamClicked",
  liveCamView = "liveCamView",
  paidLinksClicked = "paidLinksClicked",
  paidLinksViewed = "paidLinksViewed",
  revenueLinkClicked = "revenueLinkClicked",
  feedSwitched = "feedSwitched",
}

const CRITICAL_EVENTS = [
  EventTypes.gifView,
  EventTypes.gifPlayback,
  EventTypes.gifVideoLoad,
  EventTypes.gifShared,
  EventTypes.dailyVisit,
  EventTypes.liveCircleClicked,
  EventTypes.nicheSearched,
  EventTypes.profileClicked,
  EventTypes.liveCamClicked,
  EventTypes.liveCamView,
  EventTypes.paidLinksClicked,
  EventTypes.boostProfileClicked,
  EventTypes.paidLinksViewed,
  EventTypes.revenueLinkClicked,
  EventTypes.feedSwitched,
];

const ALLOWED_EVENT_PATHS = [
  "/gifs/amateur",
  "/niches/amateur-girls"
];

class LocalMetrics {
  sendGifView(props: GifViewEvent): Promise<void> {
    return this.sendEvent(EventTypes.gifView, () => {
      return apiMetrics.sendGifView({
        gif: props.gifId,
        position: props.position,
        source: props.source,
        source_id: props.sourceId,
        boosted: props.boosted,
        promoted: props.promoted,
        username: props.userName,
        fullscreen: props.fullscreen,
        pathname: location.pathname,
        context: props.context,
        reason: props.reason,
      });
    });
  }

  sendGifPlayback(props: GifPlaybackEvent): Promise<void> {
    return this.sendEvent(EventTypes.gifPlayback, () => {
      return apiMetrics.sendGifPlayback({
        username: props.userName,
        duration: props.duration, // in ms
        gif: props.gif,
        source: props.source,
        boosted: props.boosted,
        position: props.position,
        play_count: props.playCount,
        start_position: props.startPoint,
        pathname: location.pathname,
      });
    });
  }

  sendGifVideoLoad(props: GifVideoLoadEvent): Promise<void> {
    return this.sendEvent(EventTypes.gifVideoLoad, () => {
      return apiMetrics.sendGifVideoLoad({
        duration: props.duration,
        source: props.source,
      });
    });
  }

  sendGifShared(props: GifSharedEvent): Promise<void> {
    return this.sendEvent(EventTypes.gifShared, () => {
      return apiMetrics.sendGifShared({
        gif: props.gifId,
        source: props.source,
        boosted: props.boosted,
        position: props.position,
        play_count: props.playCount,
        username: props.userName,
        pathname: location.pathname,
      });
    });
  }

  sendGifAddedToCollection(props: AddedToCollectionEvent): Promise<void> {
    return this.sendEvent(EventTypes.addedToCollection, () => {
      return apiMetrics.sendGifAddedToCollection({
        gif: props.gifId,
        source: props.source,
        boosted: props.boosted,
        position: props.position,
        play_count: props.playCount,
        username: props.userName,
        pathname: location.pathname,
      });
    });
  }

  sendGifLiked(props: GifLikedEvent): Promise<void> {
    return this.sendEvent(EventTypes.gifLiked, () => {
      return apiMetrics.sendGifLiked({
        gif: props.gifId,
        source: props.source,
        boosted: props.boosted,
        position: props.position,
        play_count: props.playCount,
        username: props.userName,
        pathname: location.pathname,
      });
    });
  }

  sendProfileClicked(props: ProfileClickedEvent): Promise<void> {
    return this.sendEvent(EventTypes.profileClicked, () => {
      return apiMetrics.sendProfileClicked({
        gif: props.gifId,
        creator: props.creator,
        source: props.source,
        source_id: props.sourceId,
        position: props.position,
        play_count: props.playCount,
        username: props.userName,
        pathname: location.pathname,
        promoted: props.promoted,
      });
    });
  }

  sendFollowUserClicked(props: FollowUserClickedEvent): Promise<void> {
    return this.sendEvent(EventTypes.followUserClicked, async () => {
      if (!props.gifId) {
        return;
      }

      await apiMetrics.sendUserSubscription({
        gif: props.gifId,
        username: props.userName,
        source: props.source,
        position: props.position,
        play_count: props.playCount,
        pathname: location.pathname,
      });
    });
  }

  sendDailyVisit({ username, path }: DailyVisitEvent): Promise<void> {
    return this.sendEvent(EventTypes.dailyVisit, () => {
      return apiMetrics.sendVisit({ username, path });
    });
  }

  sendBoostProfileClicked(props: BoostProfileClickedEvent): Promise<void> {
    return this.sendEvent(EventTypes.boostProfileClicked, () => {
      return apiBoost.addProfileClick({
        campaignId: props.campaignId,
        pathname: location.pathname
      });
    });
  }

  sendLiveCircleClicked(props: LiveCircleClickedEvent): Promise<void> {
    return this.sendEvent(EventTypes.liveCircleClicked, () => {
      return apiMetrics.sendLiveCircleClicked({
        device: props.deviceType,
      });
    });
  }

  sendNicheSearched(niche: string): Promise<void> | void {
    return this.sendEvent(EventTypes.nicheSearched, () => {
      return apiMetrics.sendNicheSearched({ niche });
    });
  }

  sendLiveCamClicked(props: LiveCamClickedEvent): Promise<void> | void {
    return this.sendEvent(EventTypes.liveCamClicked, () => {
      return apiMetrics.sendLiveCamClicked(props);
    });
  }

  sendLiveCamView(props: LiveCamViewEvent): Promise<void> | void {
    return this.sendEvent(EventTypes.liveCamView, () => {
      return apiMetrics.sendLiveCamView(props);
    });
  }

  sendPaidLinksClicked({ username, source, source_id }: PaidLinksClickedEvent): Promise<void> | void {
    return this.sendEvent(EventTypes.paidLinksClicked, () => {
      return apiMetrics.sendPaidLinksClicked({ username, source, source_id });
    });
  }

  sendPaidLinksViewed({ username, source, source_id }: PaidLinksViewedEvent): Promise<void> | void {
    return this.sendEvent(EventTypes.paidLinksViewed, () => {
      return apiMetrics.sendPaidLinksViewed({ username, source, source_id });
    });
  }

  sendEmbedRevenueLinkClicked({ username, platform, gif }: EmbedRevenueLinkClickedEvent): Promise<void> | void {
    return this.sendRevenueLinkClicked({ username, platform, gif }, "embed");
  }

  sendProfileRevenueLinkClicked({ userName, platform, originGif }: SocialProfileClickedEvent): Promise<void> | void {
    if (!originGif?.id) {
      return;
    }

    return this.sendRevenueLinkClicked({ username: userName, platform, gif: originGif.id }, "profile");
  }

  sendPlayerSocialLinkClicked({ creator, platform, gif }: PlayerSocialLinkClickedEvent): Promise<void> | void {
    return this.sendRevenueLinkClicked({ username: creator, platform, gif }, "player");
  }

  sendFeedSwitched({ src, dest }: FeedSwitchedEvent): Promise<void> | void {
    return this.sendEvent(EventTypes.feedSwitched, () => {
      return apiMetrics.sendFeedSwitched({ src, dest });
    });
  }

  private sendRevenueLinkClicked(
    params: { username: string; platform: string | null; gif: string },
    linkType: RevenueLinkType,
  ): Promise<void> | void {
    return this.sendEvent(EventTypes.revenueLinkClicked, () => {
      return apiMetrics.sendRevenueLinkClicked({ ...params, link_type: linkType });
    });
  }

  private async sendEvent(eventType: EventTypes, request: () => Promise<void>) {
    if (!CRITICAL_EVENTS.includes(eventType) && !ALLOWED_EVENT_PATHS.includes(location.pathname)) {
      return;
    }

    try {
      return await request();
    } catch (err) {
      console.error("Error sending metrics.");
    }
  }
}

export default new LocalMetrics();
